import i18nFactory from '@yandex-int/i18n';

import { ApiMethodExecutingError } from '../../service/Errors';
import { Lang } from '../../typings/common';
import {
    DocCenterColumnResponse,
    DocCenterColumnTooltip,
    DocCenterData,
    DocCenterResponse,
    DocCenterTooltip
} from '../../typings/doc-center';

import * as keyset from './DocCenterRestApiMethodExecutor.i18n';

const defaultApiMethodOptions = {
    method: 'GET',
    credentials: 'same-origin',
    mode: 'cors'
};

const defaultHeaders = {
    'Content-Type': 'application/json'
};

const restI18N = i18nFactory(keyset);

export class DocCenterRestApiMethodExecutor {
    private baseUrl: string;

    constructor(baseUrl: string, private lang: Lang) {
        this.baseUrl = this.normalizeBaseUrl(baseUrl);
    }

    public getTooltipText(id: DocCenterTooltip): Promise<DocCenterData> {
        return Object.keys(DocCenterColumnTooltip).find(key => DocCenterColumnTooltip[key] === id)
            ? this.getColumnTooltipText(id)
            : this.getGeneralTooltipText(id);
    }

    private getGeneralTooltipText(id: string): Promise<DocCenterData> {
        return this.createRequest<DocCenterResponse>(`${id}.xml?output=json&isolated=1&lang=${this.lang}`).then(
            (response: DocCenterResponse) => {
                return {
                    value: response.content,
                    id: id
                };
            }
        );
    }

    private getColumnTooltipText(columnId: string): Promise<DocCenterData> {
        return this.createRequest<DocCenterColumnResponse>(
            `popups-column.xml?isolated=1&lang=${this.lang}&components=selector(query=%23${columnId})`
        ).then((response: DocCenterColumnResponse) => {
            return {
                value: response.selector['#' + columnId],
                id: columnId
            };
        });
    }

    private normalizeBaseUrl(baseUrl: string): string {
        return baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    }

    private normalizeMethodPath(methodPath: string): string {
        return methodPath.startsWith('/') ? methodPath.substr(1) : methodPath;
    }
    private getUrl(methodPath: string): string {
        return `${this.baseUrl}${this.normalizeMethodPath(methodPath)}`;
    }

    private async createRequest<T>(methodPath: string, params: RequestInit = {}): Promise<T> {
        params.headers = Object.assign({}, defaultHeaders, params.headers || {});

        const requestUrl = this.getUrl(methodPath);
        const requestParams = Object.assign({}, defaultApiMethodOptions, params);

        const request = new Request(requestUrl, requestParams);

        try {
            const response = await fetch(request);
            const data = await response.json();

            if (data.error) {
                throw {
                    name: 'requestError',
                    message: data.error
                };
            }

            return data;
        } catch (error) {
            const message = error.name === 'requestError' ? error.message : restI18N('request-error');

            throw new ApiMethodExecutingError({ message });
        }
    }
}
